import { useState, useEffect, useCallback } from "react";
import { Navbar } from "../components/Navbar";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { API_URL, API_BASE_URL } from "../config/api";
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Award,
  Target,
  AlertTriangle, 
  CheckCircle,
  Clock,
  RefreshCw,
  User,
  Calendar,
  FileText,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  BarChart3,
  PieChart,
  Download,
  Star,
  DollarSign,
  Percent,
  Package,
  Truck
} from "lucide-react";


// Classification colors and badges
const CLASSIFICATION_CONFIG = {
  "Top Performer": { color: "bg-green-500", textColor: "text-green-700", bgLight: "bg-green-100" },
  "High Performer": { color: "bg-blue-500", textColor: "text-blue-700", bgLight: "bg-blue-100" },
  "Solid Performer": { color: "bg-yellow-500", textColor: "text-yellow-700", bgLight: "bg-yellow-100" },
  "Improvement Needed": { color: "bg-orange-500", textColor: "text-orange-700", bgLight: "bg-orange-100" },
  "Performance Risk": { color: "bg-red-500", textColor: "text-red-700", bgLight: "bg-red-100" },
};

// KPI weight display
const KPI_WEIGHTS = {
  on_time: { name: "On-Time Rate", weight: 30, icon: Clock },
  margin: { name: "Margin Performance", weight: 30, icon: DollarSign },
  pricing: { name: "Pricing Accuracy", weight: 15, icon: Target },
  error: { name: "Error Rate", weight: 15, icon: AlertTriangle },
  revenue: { name: "Revenue", weight: 5, icon: TrendingUp },
  behavioral: { name: "Behavioral", weight: 5, icon: Users },
};

function ScoreGauge({ score, maxScore = 5, size = "md" }) {
  const percentage = (score / maxScore) * 100;
  const color = score >= 4 ? "text-green-500" : score >= 3 ? "text-yellow-500" : "text-red-500";
  const bgColor = score >= 4 ? "bg-green-500" : score >= 3 ? "bg-yellow-500" : "bg-red-500";
  
  const sizeClasses = {
    sm: "w-12 h-12 text-sm",
    md: "w-16 h-16 text-lg",
    lg: "w-24 h-24 text-2xl"
  };
  
  return (
    <div className={`relative ${sizeClasses[size]} rounded-full border-4 border-slate-200 flex items-center justify-center`}>
      <div 
        className={`absolute inset-0 rounded-full ${bgColor} opacity-20`}
        style={{ 
          clipPath: `polygon(50% 50%, 50% 0%, ${50 + 50 * Math.sin(percentage * 3.6 * Math.PI / 180)}% ${50 - 50 * Math.cos(percentage * 3.6 * Math.PI / 180)}%, 50% 50%)`
        }}
      />
      <span className={`font-bold ${color}`}>{score.toFixed(1)}</span>
    </div>
  );
}

function KPICard({ kpi, kpiKey }) {
  const config = KPI_WEIGHTS[kpiKey];
  const Icon = config?.icon || Target;
  const score = kpi.score || 0;
  const scoreColor = score >= 4 ? "text-green-600" : score >= 3 ? "text-yellow-600" : "text-red-600";
  
  return (
    <div className="bg-slate-50 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-medium">{config?.name || kpi.name}</span>
        </div>
        <Badge variant="outline" className="text-xs">{config?.weight}%</Badge>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <span className="text-2xl font-bold">{kpi.value !== undefined ? kpi.value : kpi.gross_margin || "-"}</span>
          <span className="text-sm text-slate-500 ml-1">{kpi.unit || ""}</span>
        </div>
        <div className={`text-xl font-bold ${scoreColor}`}>
          {score}/5
        </div>
      </div>
      {kpi.deviation !== undefined && (
        <div className={`text-xs mt-1 ${kpi.deviation >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {kpi.deviation >= 0 ? '+' : ''}{kpi.deviation}% vs target
        </div>
      )}
    </div>
  );
}

function EmployeePerformanceCard({ report, onViewDetails }) {
  const config = CLASSIFICATION_CONFIG[report.classification] || CLASSIFICATION_CONFIG["Solid Performer"];
  
  return (
    <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => onViewDetails(report)}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
              <User className="h-6 w-6 text-slate-500" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">{report.employee_name}</h3>
              <p className="text-sm text-slate-500">{report.employee_email}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{report.final_score}</div>
            <Badge className={`${config.color} text-white text-xs`}>
              {report.classification}
            </Badge>
          </div>
        </div>
        
        {/* Mini KPI bars */}
        <div className="space-y-2">
          {["on_time", "margin", "pricing", "error"].map(key => {
            const kpi = report.kpis?.[key];
            if (!kpi) return null;
            const score = kpi.score || 0;
            return (
              <div key={key} className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-20">{KPI_WEIGHTS[key]?.name}</span>
                <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${score >= 4 ? 'bg-green-500' : score >= 3 ? 'bg-yellow-500' : 'bg-red-500'}`}
                    style={{ width: `${(score / 5) * 100}%` }}
                  />
                </div>
                <span className="text-xs font-medium w-6">{score}</span>
              </div>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
          <span className="text-slate-500">
            <Package className="h-4 w-4 inline mr-1" />
            {report.summary?.total_shipments || 0} shipments
          </span>
          <span className="text-slate-500">
            <DollarSign className="h-4 w-4 inline mr-1" />
            €{(report.summary?.total_revenue || 0).toLocaleString()}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

function BehavioralEvaluationModal({ employee, month, year, onClose, onSave, token }) {
  const [evaluation, setEvaluation] = useState({
    communication: 3,
    teamwork: 3,
    initiative: 3,
    accountability: 3,
    compliance_discipline: 3,
    notes: ""
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/hr-performance/behavioral-evaluation`, {
        employee_id: employee.id,
        period_month: month,
        period_year: year,
        ...evaluation
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Behavioral evaluation saved");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to save evaluation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Behavioral Evaluation</DialogTitle>
          <DialogDescription>
            Rate {employee.name}'s behavior (1-5 scale)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {[
            { key: "communication", label: "Communication" },
            { key: "teamwork", label: "Teamwork" },
            { key: "initiative", label: "Initiative" },
            { key: "accountability", label: "Accountability" },
            { key: "compliance_discipline", label: "Compliance & Discipline" }
          ].map(({ key, label }) => (
            <div key={key}>
              <Label className="text-sm">{label}</Label>
              <div className="flex gap-2 mt-1">
                {[1, 2, 3, 4, 5].map(score => (
                  <button
                    key={score}
                    onClick={() => setEvaluation({ ...evaluation, [key]: score })}
                    className={`w-10 h-10 rounded-full border-2 font-medium transition-colors ${
                      evaluation[key] === score 
                        ? 'bg-blue-500 text-white border-blue-500' 
                        : 'border-slate-300 hover:border-blue-300'
                    }`}
                  >
                    {score}
                  </button>
                ))}
              </div>
            </div>
          ))}
          
          <div>
            <Label className="text-sm">Notes (optional)</Label>
            <Textarea 
              value={evaluation.notes}
              onChange={(e) => setEvaluation({ ...evaluation, notes: e.target.value })}
              placeholder="Additional comments..."
              rows={3}
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save Evaluation
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OperationalDataModal({ employee, month, year, onClose, onSave, token }) {
  const [data, setData] = useState({
    total_shipments: 0,
    on_time_shipments: 0,
    total_revenue: 0,
    total_transport_cost: 0,
    shipments_with_errors: 0,
    shipments_with_pricing_error: 0,
    target_margin_percentage: 15,
    target_revenue: 0
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/hr-performance/operational-data`, {
        employee_id: employee.id,
        period_month: month,
        period_year: year,
        ...data
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Operational data saved");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to save data");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Operational Data Entry</DialogTitle>
          <DialogDescription>
            Enter operational metrics for {employee.name} - {year}/{month}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Total Shipments</Label>
            <Input 
              type="number" 
              value={data.total_shipments}
              onChange={(e) => setData({ ...data, total_shipments: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>On-Time Shipments</Label>
            <Input 
              type="number" 
              value={data.on_time_shipments}
              onChange={(e) => setData({ ...data, on_time_shipments: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>Total Revenue (€)</Label>
            <Input 
              type="number" 
              value={data.total_revenue}
              onChange={(e) => setData({ ...data, total_revenue: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>Total Transport Cost (€)</Label>
            <Input 
              type="number" 
              value={data.total_transport_cost}
              onChange={(e) => setData({ ...data, total_transport_cost: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>Shipments with Errors</Label>
            <Input 
              type="number" 
              value={data.shipments_with_errors}
              onChange={(e) => setData({ ...data, shipments_with_errors: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>Shipments with Pricing Error</Label>
            <Input 
              type="number" 
              value={data.shipments_with_pricing_error}
              onChange={(e) => setData({ ...data, shipments_with_pricing_error: parseInt(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>Target Margin %</Label>
            <Input 
              type="number" 
              value={data.target_margin_percentage}
              onChange={(e) => setData({ ...data, target_margin_percentage: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <Label>Target Revenue (€)</Label>
            <Input 
              type="number" 
              value={data.target_revenue}
              onChange={(e) => setData({ ...data, target_revenue: parseFloat(e.target.value) || 0 })}
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Data
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function HRPerformancePage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Period selection
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  
  // Data
  const [reports, setReports] = useState([]);
  const [classifications, setClassifications] = useState({});
  const [topPerformers, setTopPerformers] = useState([]);
  const [needsAttention, setNeedsAttention] = useState([]);
  const [staff, setStaff] = useState([]);
  
  // Modals
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showBehavioralModal, setShowBehavioralModal] = useState(false);
  const [showOperationalModal, setShowOperationalModal] = useState(false);
  const [detailReport, setDetailReport] = useState(null);

  const monthNames = ["", "January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}/hr-performance/reports/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReports(response.data.reports || []);
      setClassifications(response.data.classifications || {});
      setTopPerformers(response.data.top_performers || []);
      setNeedsAttention(response.data.needs_attention || []);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
      toast.error("Failed to load performance reports");
    } finally {
      setLoading(false);
    }
  }, [token, selectedYear, selectedMonth]);

  const fetchStaff = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/staff-directory`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStaff(response.data.staff || []);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
    }
  }, [token]);

  const calculatePerformance = async (employeeId) => {
    try {
      const response = await axios.get(
        `${API_URL}/hr-performance/calculate/${employeeId}/${selectedYear}/${selectedMonth}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Performance calculated");
      fetchReports();
      return response.data;
    } catch (error) {
      toast.error("Failed to calculate performance");
    }
  };

  useEffect(() => {
    fetchReports();
    fetchStaff();
  }, [fetchReports, fetchStaff]);

  const navigateMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  // Summary stats
  const avgScore = reports.length > 0 
    ? (reports.reduce((sum, r) => sum + (r.final_score || 0), 0) / reports.length).toFixed(2)
    : 0;
  const totalRevenue = reports.reduce((sum, r) => sum + (r.summary?.total_revenue || 0), 0);
  const totalShipments = reports.reduce((sum, r) => sum + (r.summary?.total_shipments || 0), 0);

  if (loading && reports.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Award className="h-6 w-6" />
                HR Performance Management
              </h1>
              <p className="text-blue-200 text-sm mt-1">
                Monthly staff performance tracking and evaluation
              </p>
            </div>
            
            {/* Month Navigator */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(-1)}
                className="text-white hover:bg-white/20"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="text-center min-w-[150px]">
                <div className="text-lg font-semibold">{monthNames[selectedMonth]} {selectedYear}</div>
                <div className="text-xs text-blue-200">Performance Period</div>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(1)}
                className="text-white hover:bg-white/20"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
              <Button 
                variant="outline" 
                onClick={fetchReports} 
                className="text-white border-white/30 hover:bg-white/10 ml-4"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Employees</p>
                  <p className="text-3xl font-bold">{reports.length}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avg Score</p>
                  <p className="text-3xl font-bold">{avgScore}</p>
                </div>
                <Star className="h-10 w-10 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Top Performers</p>
                  <p className="text-3xl font-bold text-green-600">{classifications["Top Performer"] || 0}</p>
                </div>
                <Award className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={needsAttention.length > 0 ? "border-orange-300 bg-orange-50" : ""}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Needs Attention</p>
                  <p className="text-3xl font-bold text-orange-600">{needsAttention.length}</p>
                </div>
                <AlertTriangle className="h-10 w-10 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Revenue</p>
                  <p className="text-2xl font-bold">€{totalRevenue.toLocaleString()}</p>
                </div>
                <DollarSign className="h-10 w-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="rankings">Rankings</TabsTrigger>
            <TabsTrigger value="data-entry">Data Entry</TabsTrigger>
            <TabsTrigger value="bonus">Bonus Report</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            {/* Classification Distribution */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Performance Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  {Object.entries(CLASSIFICATION_CONFIG).map(([cls, config]) => (
                    <div key={cls} className="flex-1 text-center">
                      <div className={`${config.bgLight} rounded-lg p-4`}>
                        <div className={`text-3xl font-bold ${config.textColor}`}>
                          {classifications[cls] || 0}
                        </div>
                        <div className="text-xs text-slate-600 mt-1">{cls}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Employee Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {reports.length === 0 ? (
                <Card className="col-span-full">
                  <CardContent className="py-12 text-center text-slate-500">
                    <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>No performance reports for this period</p>
                    <p className="text-sm mt-2">Go to Data Entry tab to add operational data</p>
                  </CardContent>
                </Card>
              ) : (
                reports.map(report => (
                  <EmployeePerformanceCard 
                    key={report.employee_id} 
                    report={report}
                    onViewDetails={setDetailReport}
                  />
                ))
              )}
            </div>
          </TabsContent>

          {/* Rankings Tab */}
          <TabsContent value="rankings">
            <Card>
              <CardHeader>
                <CardTitle>Performance Rankings - {monthNames[selectedMonth]} {selectedYear}</CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Rank</th>
                      <th className="text-left py-3 px-4">Employee</th>
                      <th className="text-center py-3 px-4">Score</th>
                      <th className="text-center py-3 px-4">On-Time</th>
                      <th className="text-center py-3 px-4">Margin</th>
                      <th className="text-center py-3 px-4">Errors</th>
                      <th className="text-left py-3 px-4">Classification</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((report, idx) => {
                      const config = CLASSIFICATION_CONFIG[report.classification] || CLASSIFICATION_CONFIG["Solid Performer"];
                      return (
                        <tr key={report.employee_id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                              idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                              idx === 1 ? 'bg-slate-300 text-slate-700' :
                              idx === 2 ? 'bg-orange-300 text-orange-800' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {idx + 1}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="font-medium">{report.employee_name}</div>
                            <div className="text-sm text-slate-500">{report.employee_email}</div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className="text-xl font-bold">{report.final_score}</span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={report.kpis?.on_time?.score >= 4 ? 'text-green-600' : report.kpis?.on_time?.score >= 3 ? 'text-yellow-600' : 'text-red-600'}>
                              {report.kpis?.on_time?.value || 0}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={report.kpis?.margin?.score >= 4 ? 'text-green-600' : report.kpis?.margin?.score >= 3 ? 'text-yellow-600' : 'text-red-600'}>
                              {report.kpis?.margin?.gross_margin || 0}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={report.kpis?.error?.score >= 4 ? 'text-green-600' : report.kpis?.error?.score >= 3 ? 'text-yellow-600' : 'text-red-600'}>
                              {report.kpis?.error?.value || 0}%
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={`${config.color} text-white`}>
                              {report.classification}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Data Entry Tab */}
          <TabsContent value="data-entry">
            <Card>
              <CardHeader>
                <CardTitle>Data Entry & Calculation</CardTitle>
                <CardDescription>
                  Enter operational data and behavioral evaluations for staff members
                </CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Employee</th>
                      <th className="text-center py-3 px-4">Operational Data</th>
                      <th className="text-center py-3 px-4">Behavioral Eval</th>
                      <th className="text-center py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staff.map(employee => {
                      const hasReport = reports.find(r => r.employee_id === employee.id);
                      return (
                        <tr key={employee.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="font-medium">{employee.full_name || employee.name}</div>
                            <div className="text-sm text-slate-500">{employee.email}</div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setShowOperationalModal(true);
                              }}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              Enter Data
                            </Button>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedEmployee(employee);
                                setShowBehavioralModal(true);
                              }}
                            >
                              <Users className="h-4 w-4 mr-1" />
                              Evaluate
                            </Button>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Button
                              size="sm"
                              onClick={() => calculatePerformance(employee.id)}
                            >
                              <BarChart3 className="h-4 w-4 mr-1" />
                              Calculate
                            </Button>
                            {hasReport && (
                              <Badge className="ml-2 bg-green-500">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Done
                              </Badge>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bonus Tab */}
          <TabsContent value="bonus">
            <Card>
              <CardHeader>
                <CardTitle>Bonus Eligibility - {monthNames[selectedMonth]} {selectedYear}</CardTitle>
                <CardDescription>
                  Employees with Final Score ≥4.0, Margin Score ≥3, Error Score ≥3 are eligible
                </CardDescription>
              </CardHeader>
              <CardContent>
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Employee</th>
                      <th className="text-center py-3 px-4">Final Score</th>
                      <th className="text-center py-3 px-4">Eligible</th>
                      <th className="text-right py-3 px-4">Margin Contribution</th>
                      <th className="text-center py-3 px-4">Multiplier</th>
                      <th className="text-right py-3 px-4">Bonus Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(report => {
                      const bonus = report.bonus || {};
                      return (
                        <tr key={report.employee_id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="font-medium">{report.employee_name}</div>
                          </td>
                          <td className="py-3 px-4 text-center font-bold">{report.final_score}</td>
                          <td className="py-3 px-4 text-center">
                            {bonus.eligible ? (
                              <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" />Yes</Badge>
                            ) : (
                              <Badge variant="destructive">No</Badge>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            €{(report.summary?.margin_contribution || 0).toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-center">
                            {bonus.eligible ? `${(bonus.multiplier * 100).toFixed(0)}%` : '-'}
                          </td>
                          <td className="py-3 px-4 text-right font-bold text-green-600">
                            {bonus.eligible ? `€${bonus.bonus_amount?.toLocaleString() || 0}` : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-100 font-bold">
                      <td colSpan="5" className="py-3 px-4 text-right">Total Bonus Pool:</td>
                      <td className="py-3 px-4 text-right text-green-600">
                        €{reports.reduce((sum, r) => sum + (r.bonus?.bonus_amount || 0), 0).toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Detail Report Modal */}
      {detailReport && (
        <Dialog open onOpenChange={() => setDetailReport(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                {detailReport.employee_name} - Performance Report
              </DialogTitle>
              <DialogDescription>
                {monthNames[detailReport.period_month]} {detailReport.period_year}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Final Score */}
              <div className="flex items-center justify-between bg-slate-50 rounded-lg p-6">
                <div>
                  <p className="text-sm text-slate-500">Final Performance Score</p>
                  <p className="text-5xl font-bold">{detailReport.final_score}</p>
                </div>
                <Badge className={`${CLASSIFICATION_CONFIG[detailReport.classification]?.color || 'bg-slate-500'} text-white text-lg py-2 px-4`}>
                  {detailReport.classification}
                </Badge>
              </div>

              {/* KPIs Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {Object.entries(detailReport.kpis || {}).map(([key, kpi]) => (
                  <KPICard key={key} kpi={kpi} kpiKey={key} />
                ))}
              </div>

              {/* Summary */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 text-center">
                    <Package className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{detailReport.summary?.total_shipments || 0}</p>
                    <p className="text-sm text-slate-500">Total Shipments</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <DollarSign className="h-6 w-6 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">€{(detailReport.summary?.total_revenue || 0).toLocaleString()}</p>
                    <p className="text-sm text-slate-500">Total Revenue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 text-center">
                    <TrendingUp className="h-6 w-6 mx-auto text-purple-500 mb-2" />
                    <p className="text-2xl font-bold">€{(detailReport.summary?.margin_contribution || 0).toLocaleString()}</p>
                    <p className="text-sm text-slate-500">Margin Contribution</p>
                  </CardContent>
                </Card>
              </div>

              {/* Bonus Info */}
              {detailReport.bonus && (
                <Card className={detailReport.bonus.eligible ? 'border-green-300 bg-green-50' : ''}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Bonus Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {detailReport.bonus.eligible ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <Badge className="bg-green-500 mb-2">Eligible for Bonus</Badge>
                          <p className="text-sm text-slate-600">
                            Base: €{detailReport.bonus.base_bonus?.toLocaleString()} × 
                            {(detailReport.bonus.multiplier * 100).toFixed(0)}% multiplier
                          </p>
                        </div>
                        <div className="text-3xl font-bold text-green-600">
                          €{detailReport.bonus.bonus_amount?.toLocaleString()}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Badge variant="destructive" className="mb-2">Not Eligible</Badge>
                        <p className="text-sm text-slate-600">{detailReport.bonus.reason}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Behavioral Evaluation Modal */}
      {showBehavioralModal && selectedEmployee && (
        <BehavioralEvaluationModal
          employee={selectedEmployee}
          month={selectedMonth}
          year={selectedYear}
          onClose={() => setShowBehavioralModal(false)}
          onSave={fetchReports}
          token={token}
        />
      )}

      {/* Operational Data Modal */}
      {showOperationalModal && selectedEmployee && (
        <OperationalDataModal
          employee={selectedEmployee}
          month={selectedMonth}
          year={selectedYear}
          onClose={() => setShowOperationalModal(false)}
          onSave={fetchReports}
          token={token}
        />
      )}
    </div>
  );
}
